import useRequest from "@/lib/request/useRequest";
import { useMemo, useState, useCallback } from "react";
import { WaterRiskZones } from "@/types/water";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { scaleTime } from "d3-scale";
import {
    Switch,
    RangeValue,
    DateValue,
    ButtonGroup,
    Button,
} from "@heroui/react";
import { useI18n } from "@/lib/i18n/useI18n";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    Area,
    Line,
    Tooltip,
    Legend,
} from "recharts";
import dayjs from "dayjs";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

function SoilWaterContent({
    fieldId,
    dateRange,
}: {
    fieldId: string | undefined;
    dateRange: RangeValue<DateValue> | null;
}) {
    const { useGetRequest } = useRequest();
    const { formatDateMMDDYYYY, formatUnit } = useI18n();
    const { t } = useTranslation();
    const waterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
    });

    const dataInRange = useMemo(() => {
        return (
            waterContentQuery.data?.data?.filter(
                (d) =>
                    dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                    dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
            ) ?? []
        );
    }, [waterContentQuery.data?.data, dateRange]);

    const availableWaterContentData = useMemo(() => {
        return dataInRange.map((d) => {
            return {
                ...d,
                time: dayjs(d.time).toDate().getTime(),
                availableWaterContent: d.swc_est ? d.swc_est - d.wp : null,
                saturated: [d.fhc - d.wp, d.rtsml_t - d.wp],
                optimal: [d.rtsml_b - d.wp, d.rtsml_t - d.wp],
                pink: [0, d.rtsml_b - d.wp],
                fcLine: d.fhc - d.wp,
            };
        });
    }, [dataInRange]);

    const moistureSufficiencyData = useMemo(() => {
        return dataInRange.map((d) => {
            const taw = d.fhc - d.wp;
            const suff = d.swc_est ? ((d.swc_est - d.wp) / taw) * 100 : null;
            const mad = taw
                ? Math.min(Math.max(23.2 + 15.5 * Math.log(taw), 35), 60)
                : null;
            return {
                ...d,
                time: dayjs(d.time).toDate().getTime(),
                taw,
                red: mad ? [0, 0.5 * mad] : null,
                yellow: mad ? [0.5 * mad, mad] : null,
                green: mad ? [mad, 100] : null,
                blue: [100, 1000],
                suff,
            };
        });
    }, [dataInRange]);

    const [sufficiencyMin, sufficiencyMax] = useMemo(() => {
        return [
            0,
            Math.max(
                100,
                Math.max(
                    ...moistureSufficiencyData
                        .filter((d) => d.suff !== null)
                        .map((d) => d.suff as number),
                ),
            ),
        ];
    }, [moistureSufficiencyData]);

    const waterContentData = useMemo(() => {
        return sortArrayByKey(
            dataInRange.map((d) => ({
                ...d,
                time: dayjs(d.time).toDate().getTime(),
                range: [d.swc_sig_under, d.swc_sig_over],
                green: [d.rtsml_b, d.rtsml_t],
                red: [d.wp, d.rtsml_b],
                blue: [d.rtsml_t, d.fhc],
                gray: [0, d.wp],
            })) ?? [],
            "time",
        );
    }, [dataInRange]);

    const { setSelectedDate } = useFieldDashboard() ?? {};
    const [showSWCRanges, setShowSWCRanges] = useState(false);

    const maxValue = Math.max(
        Math.ceil(
            Math.max(...waterContentData.map((d) => d.time as number)) / 2,
        ) * 2,
    );
    const minValue = Math.min(...waterContentData.map((d) => d.time as number));
    const timeScale = scaleTime().domain([minValue, maxValue]);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const [minSoilWaterContent, maxSoilWaterContent] = useMemo(() => {
        return [
            Math.min(
                ...waterContentData
                    .filter((d) => d.wp !== null)
                    .map((d) => d.wp as number),
            ) - 1,
            Math.max(
                ...waterContentData
                    .filter((d) => d.swc_sig_over !== null)
                    .map((d) => d.swc_sig_over as number),
            ),
        ];
    }, [waterContentData]);

    const onChartClick: CategoricalChartFunc = useCallback(
        (data) => {
            const date = dayjs(data.activePayload?.[0]?.payload?.time).format(
                "YYYY-MM-DD",
            );
            if (date) {
                setSelectedDate?.(date);
            }
        },
        [setSelectedDate],
    );

    const [graphType, setGraphType] = useState<
        "waterContent" | "availableWaterContent" | "moistureSufficiency"
    >("availableWaterContent");

    return (
        <div>
            <div className="font-semibold">Water startContent</div>
            <div className="flex flex-row items-center gap-2 justify-between">
                <div>
                    <ButtonGroup size="sm">
                        <Button
                            color={
                                graphType === "availableWaterContent"
                                    ? "primary"
                                    : "default"
                            }
                            onPress={() =>
                                setGraphType("availableWaterContent")
                            }
                        >
                            Available Water
                        </Button>

                        <Button
                            color={
                                graphType === "moistureSufficiency"
                                    ? "primary"
                                    : "default"
                            }
                            onPress={() => setGraphType("moistureSufficiency")}
                        >
                            Moisture Sufficiency
                        </Button>
                        <Button
                            color={
                                graphType === "waterContent"
                                    ? "primary"
                                    : "default"
                            }
                            onPress={() => setGraphType("waterContent")}
                        >
                            Total Water
                        </Button>
                    </ButtonGroup>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <div className="text-sm">Uncertainty Threshold</div>
                    <Switch
                        size="sm"
                        aria-label="Uncertainty Threshold"
                        isSelected={showSWCRanges}
                        onValueChange={setShowSWCRanges}
                    />
                </div>
            </div>
            {waterContentData.length ? (
                <>
                    {graphType === "waterContent" && (
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={waterContentData}
                                syncId="water"
                                onClick={onChartClick}
                                style={{ cursor: "pointer" }}
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    fontSize={12}
                                    label={{
                                        value: t(
                                            "chartLabels.fieldSoilWaterContent",
                                            {
                                                ns: "insights",
                                            },
                                        ),
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    domain={[
                                        Math.floor(minSoilWaterContent),
                                        Math.ceil(maxSoilWaterContent),
                                    ]}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                />
                                <Area
                                    dataKey="red"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.nearingDepletion", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="red"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="blue"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.nearingSaturation", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="blue"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="green"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.optimal", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="green"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="gray"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="gray"
                                    fillOpacity={0.1}
                                    name="Wilting Point"
                                    tooltipType="none"
                                    stroke="gray"
                                    legendType="rect"
                                />
                                {showSWCRanges && (
                                    <>
                                        <Area
                                            dataKey="range"
                                            dot={false}
                                            strokeWidth={0}
                                            fill="black"
                                            fillOpacity={0.2}
                                            tooltipType="none"
                                            legendType="none"
                                        />
                                    </>
                                )}

                                <Line
                                    dataKey="swc_est"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={3}
                                    name={t("chartLabels.soilWaterContent", {
                                        ns: "insights",
                                    })}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                    {graphType == "availableWaterContent" && (
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={availableWaterContentData}
                                syncId="water"
                                style={{ cursor: "pointer" }}
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    fontSize={12}
                                    label={{
                                        value: "Available Water Content",
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                />
                                <Line
                                    dataKey="fcLine"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    name="Field Capacity"
                                    tooltipType="none"
                                    legendType="plainline"
                                />
                                <Area
                                    dataKey="saturated"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    legendType="rect"
                                    stroke="blue"
                                    name="Saturated"
                                />
                                <Area
                                    dataKey="optimal"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    legendType="rect"
                                    stroke="green"
                                    name="Optimal"
                                />
                                <Area
                                    dataKey="pink"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    legendType="rect"
                                    stroke="red"
                                    name="Depletion"
                                />
                                <Line
                                    dataKey="availableWaterContent"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={3}
                                    name="Available Water Content"
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                    {graphType === "moistureSufficiency" && (
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={moistureSufficiencyData}
                                syncId="water"
                                style={{ cursor: "pointer" }}
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    fontSize={12}
                                    label={{
                                        value: "% of Field Capacity",
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                    domain={[sufficiencyMin, sufficiencyMax]}
                                />
                                <Area
                                    dataKey="red"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Depleted"
                                    stroke="red"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="yellow"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="yellow"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Nearing Depletion"
                                    stroke="#b59d19"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="green"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    stroke="green"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Sufficient"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="blue"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Saturated"
                                    stroke="blue"
                                    legendType="rect"
                                />
                                <Line
                                    dataKey="suff"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={2}
                                    name="Moisture Sufficiency"
                                    tooltipType="none"
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                </>
            ) : (
                <div className="text-sm italic text-center mt-4">
                    No data available
                </div>
            )}
        </div>
    );
}

export default SoilWaterContent;
